/**
 * EncounterAnalysisSidebar Component
 *
 * A sidebar-optimized view of encounter analysis data (~450px width).
 * Uses compact cards, status chips, truncation, and visual hierarchy
 * designed for a vertical scrolling sidebar panel.
 */

import React, { useState } from 'react';
import {
  Assignment as AssignmentIcon,
  CheckCircleOutlined as CheckIcon,
  Description as DescriptionIcon,
  ErrorOutlined as ErrorIcon,
  ExpandLess as CollapseIcon,
  ExpandMore as ExpandIcon,
  FolderOpen as DataIcon,
  Groups as GroupsIcon,
  Help as HelpIcon,
  LocalHospital as ClinicalIcon,
  LocalPharmacy as MedsIcon,
  MedicalInformation as DiagnosisIcon,
  NewReleases as NewIcon,
  Place as PlaceIcon,
  Psychology as PsychIcon,
  Shield as ShieldIcon,
  TaskAlt as TaskIcon,
  TrendingDown as WorseIcon,
  TrendingFlat as StableIcon,
  TrendingUp as ImprovingIcon,
  Videocam as VideocamIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  alpha,
  Box,
  ButtonBase,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatSnakeCase = (str: string): string =>
  str
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

const PROBLEM_STATUS_CONFIG: Record<
  string,
  { icon: React.ElementType; color: string; label: string }
> = {
  stable: { icon: StableIcon, label: 'Stable', color: 'success' },
  improving: { icon: ImprovingIcon, label: 'Improving', color: 'success' },
  not_at_goal: { icon: WarningIcon, label: 'Not at Goal', color: 'warning' },
  worsening: { icon: WorseIcon, label: 'Worsening', color: 'error' },
  new: { icon: NewIcon, label: 'New', color: 'info' },
  resolved: { icon: CheckIcon, label: 'Resolved', color: 'success' },
  unknown: { icon: HelpIcon, label: 'Unknown', color: 'default' },
};

const SAFETY_STATUS_CONFIG: Record<
  string,
  { icon: React.ElementType; color: string; label: string; bgColor: string }
> = {
  no_concerns: { icon: CheckIcon, label: 'No Concerns', color: 'success', bgColor: 'success' },
  concerns_present: { icon: ErrorIcon, label: 'Concerns Present', color: 'error', bgColor: 'error' },
  not_assessed: { icon: HelpIcon, label: 'Not Assessed', color: 'default', bgColor: 'grey' },
  unknown: { icon: HelpIcon, label: 'Unknown', color: 'default', bgColor: 'grey' },
};

// ─── Sub-components ─────────────────────────────────────────────────────────

/** Section card with icon header — the main visual block in the sidebar */
const SidebarCard: React.FC<{
  icon: React.ReactNode;
  title: string;
  badge?: React.ReactNode;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  collapsible?: boolean;
}> = ({ icon, title, badge, children, defaultExpanded = true, collapsible = true }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <Box
      sx={{
        border: `1px solid ${theme.palette.grey[200]}`,
        borderRadius: 2,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <ButtonBase
        onClick={collapsible ? () => setExpanded(prev => !prev) : undefined}
        component="div"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          width: '100%',
          px: 1.5,
          py: 1,
          bgcolor: theme.palette.grey[50],
          borderBottom: expanded ? `1px solid ${theme.palette.grey[200]}` : 'none',
          cursor: collapsible ? 'pointer' : 'default',
          textAlign: 'left',
          '&:hover': collapsible ? { bgcolor: theme.palette.grey[100] } : {},
          transition: 'background-color 0.15s',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 24,
            height: 24,
            borderRadius: '6px',
            bgcolor: alpha(theme.palette.primary.main, 0.08),
            color: theme.palette.primary.main,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            flex: 1,
            fontSize: '0.7rem',
          }}
        >
          {title}
        </Typography>
        {badge}
        {collapsible && (
          expanded ? (
            <CollapseIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
          ) : (
            <ExpandIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />
          )
        )}
      </ButtonBase>

      {/* Content */}
      {expanded && <Box sx={{ p: 1.5 }}>{children}</Box>}
    </Box>
  );
};

/** Truncated text with show more/less */
const TruncatedText: React.FC<{
  text: string;
  maxLines?: number;
}> = ({ text, maxLines = 3 }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);

  return (
    <Box>
      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          lineHeight: 1.55,
          fontSize: '0.8rem',
          ...(!expanded && {
            display: '-webkit-box',
            WebkitLineClamp: maxLines,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }),
        }}
      >
        {text}
      </Typography>
      <ButtonBase
        onClick={() => setExpanded(prev => !prev)}
        sx={{
          color: theme.palette.primary.main,
          fontSize: '0.72rem',
          fontWeight: 600,
          mt: 0.25,
          py: 0.25,
          borderRadius: 0.5,
          '&:hover': { textDecoration: 'underline' },
        }}
      >
        {expanded ? 'Show less' : 'Show more'}
      </ButtonBase>
    </Box>
  );
};

/** Stat pill — compact key-value display */
const StatPill: React.FC<{
  label: string;
  value: string | number;
  color?: string;
}> = ({ label, value, color }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        px: 1.5,
        py: 0.75,
        bgcolor: theme.palette.grey[50],
        borderRadius: 1.5,
      }}
    >
      <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 500 }}>
        {label}
      </Typography>
      <Typography
        variant="caption"
        sx={{ fontWeight: 700, color: color ?? theme.palette.text.primary }}
      >
        {value}
      </Typography>
    </Box>
  );
};

// ─── Main component ─────────────────────────────────────────────────────────

interface EncounterAnalysisSidebarProps {
  /** The encounter analysis data (rich JSON from backend) */
  encounterAnalysis: Record<string, any> | null | undefined;
}

export const EncounterAnalysisSidebar: React.FC<EncounterAnalysisSidebarProps> = ({
  encounterAnalysis,
}) => {
  const theme = useTheme();

  if (!encounterAnalysis) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
          No encounter analysis available
        </Typography>
      </Box>
    );
  }

  const {
    summary,
    canonical_service_type,
    session_type,
    participants,
    problems_addressed,
    treatment_modalities,
    risk_assessment,
    clinical_markers,
    data_reviewed,
    activities_performed,
    attestations,
    treatment_facts,
    encounter_mode,
    place_of_care,
  } = encounterAnalysis as any;

  // Prefer canonical_service_type.name, fall back to session_type.canonical
  const serviceTypeName: string | null =
    canonical_service_type?.name || session_type?.canonical || null;
  const serviceTypeDescription: string | null =
    canonical_service_type?.description || null;
  const clinicTerms: string[] =
    session_type?.clinic_terms || [];

  const safetyConfig = risk_assessment?.safety_status
    ? SAFETY_STATUS_CONFIG[risk_assessment.safety_status] ?? SAFETY_STATUS_CONFIG.unknown
    : null;

  // Problem status breakdown for the visual bar
  const problemStatusCounts = problems_addressed
    ? problems_addressed.reduce(
        (acc: Record<string, number>, p: any) => {
          const status = p.status || 'unknown';
          acc[status] = (acc[status] || 0) + 1;
          return acc;
        },
        {} as Record<string, number>,
      )
    : {};

  const totalProblems = problems_addressed?.length ?? 0;

  const hasTreatmentFacts =
    treatment_facts &&
    (treatment_facts.medications?.length > 0 ||
      treatment_facts.supplements?.length > 0 ||
      treatment_facts.procedures?.length > 0);

  const totalMeds = (treatment_facts?.medications?.length ?? 0) +
    (treatment_facts?.supplements?.length ?? 0);

  return (
    <Stack spacing={1.5}>
      {/* ── Metadata chips ── */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
        {serviceTypeName && (
          <Chip
            icon={<AssignmentIcon sx={{ fontSize: 13 }} />}
            label={serviceTypeName}
            size="small"
            variant="outlined"
            sx={{ fontWeight: 600, fontSize: '0.68rem', height: 24 }}
          />
        )}
        {encounter_mode && (
          <Chip
            icon={<VideocamIcon sx={{ fontSize: 13 }} />}
            label={encounter_mode}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.68rem', height: 24 }}
          />
        )}
        {place_of_care && (
          <Chip
            icon={<PlaceIcon sx={{ fontSize: 13 }} />}
            label={formatSnakeCase(place_of_care)}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.68rem', height: 24 }}
          />
        )}
        {clinicTerms.map((term: string, idx: number) => (
          <Chip
            key={idx}
            label={term}
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              fontSize: '0.65rem',
              height: 22,
            }}
          />
        ))}
      </Box>

      {/* ── Quick stats row ── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
        <StatPill label="Problems" value={totalProblems} />
        <StatPill
          label="Safety"
          value={safetyConfig?.label ?? '—'}
          color={
            safetyConfig?.color === 'error'
              ? theme.palette.error.main
              : safetyConfig?.color === 'success'
                ? theme.palette.success.main
                : undefined
          }
        />
        <StatPill label="Medications" value={totalMeds} />
        <StatPill label="Activities" value={activities_performed?.length ?? 0} />
      </Box>

      {/* ── Summary ── */}
      {summary && (
        <SidebarCard
          icon={<DescriptionIcon sx={{ fontSize: 15 }} />}
          title="Summary"
          defaultExpanded
        >
          <TruncatedText text={summary} maxLines={3} />
        </SidebarCard>
      )}

      {/* ── Problems Addressed ── */}
      {problems_addressed && problems_addressed.length > 0 && (
        <SidebarCard
          icon={<DiagnosisIcon sx={{ fontSize: 15 }} />}
          title="Problems"
          badge={
            <Chip
              label={totalProblems}
              size="small"
              sx={{
                height: 18,
                minWidth: 18,
                fontSize: '0.65rem',
                fontWeight: 700,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              }}
            />
          }
          defaultExpanded
        >
          {/* Status breakdown bar */}
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', borderRadius: 1, overflow: 'hidden', height: 6 }}>
              {Object.entries(problemStatusCounts).map(([status, count]) => {
                const config = PROBLEM_STATUS_CONFIG[status] ?? PROBLEM_STATUS_CONFIG.unknown;
                const pct = ((count as number) / totalProblems) * 100;
                return (
                  <Box
                    key={status}
                    sx={{
                      width: `${pct}%`,
                      bgcolor:
                        theme.palette[config.color as 'success' | 'warning' | 'error' | 'info']
                          ?.main ?? theme.palette.grey[400],
                    }}
                  />
                );
              })}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, mt: 0.5 }}>
              {Object.entries(problemStatusCounts).map(([status, count]) => {
                const config = PROBLEM_STATUS_CONFIG[status] ?? PROBLEM_STATUS_CONFIG.unknown;
                return (
                  <Typography
                    key={status}
                    variant="caption"
                    sx={{
                      fontSize: '0.6rem',
                      color: theme.palette.text.secondary,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.25,
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor:
                          theme.palette[config.color as 'success' | 'warning' | 'error' | 'info']
                            ?.main ?? theme.palette.grey[400],
                        display: 'inline-block',
                      }}
                    />
                    {count as number} {config.label}
                  </Typography>
                );
              })}
            </Box>
          </Box>

          {/* Problem list */}
          <Stack spacing={1} divider={<Divider />}>
            {problems_addressed.map((problem: any, idx: number) => {
              const sc = PROBLEM_STATUS_CONFIG[problem.status] ?? PROBLEM_STATUS_CONFIG.unknown;
              const StatusIcon = sc.icon;
              return (
                <Box key={idx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <StatusIcon
                      sx={{
                        fontSize: 14,
                        color:
                          theme.palette[sc.color as 'success' | 'warning' | 'error' | 'info']
                            ?.main ?? theme.palette.grey[500],
                      }}
                    />
                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600, fontSize: '0.78rem', flex: 1 }}
                    >
                      {problem.problem}
                    </Typography>
                    <Chip
                      label={sc.label}
                      size="small"
                      color={sc.color as any}
                      variant="outlined"
                      sx={{ height: 18, fontSize: '0.6rem', fontWeight: 600 }}
                    />
                  </Box>
                  {problem.details && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: theme.palette.text.secondary,
                        lineHeight: 1.4,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        pl: 2.5,
                        mt: 0.25,
                        fontSize: '0.72rem',
                      }}
                    >
                      {problem.details}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Stack>
        </SidebarCard>
      )}

      {/* ── Risk Assessment ── */}
      {risk_assessment && safetyConfig && (
        <SidebarCard
          icon={<ShieldIcon sx={{ fontSize: 15 }} />}
          title="Risk Assessment"
          badge={
            <Chip
              icon={<safetyConfig.icon sx={{ fontSize: 13 }} />}
              label={safetyConfig.label}
              size="small"
              color={safetyConfig.color as any}
              sx={{ height: 20, fontSize: '0.6rem', fontWeight: 600 }}
            />
          }
          defaultExpanded={safetyConfig.color === 'error'}
        >
          {risk_assessment.details && (
            <TruncatedText text={risk_assessment.details} maxLines={3} />
          )}
        </SidebarCard>
      )}

      {/* ── Medications & Treatment ── */}
      {hasTreatmentFacts && (
        <SidebarCard
          icon={<MedsIcon sx={{ fontSize: 15 }} />}
          title="Medications & Treatment"
          badge={
            <Chip
              label={totalMeds}
              size="small"
              sx={{
                height: 18,
                minWidth: 18,
                fontSize: '0.65rem',
                fontWeight: 700,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              }}
            />
          }
          defaultExpanded={false}
        >
          <Stack spacing={0.5}>
            {treatment_facts.medications?.map((med: string, idx: number) => (
              <Box key={`m-${idx}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    bgcolor: theme.palette.primary.main,
                    mt: '7px',
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.45 }}>
                  {med}
                </Typography>
              </Box>
            ))}
            {treatment_facts.supplements?.map((sup: string, idx: number) => (
              <Box key={`s-${idx}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    bgcolor: theme.palette.info.main,
                    mt: '7px',
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.45 }}>
                  {sup}
                </Typography>
              </Box>
            ))}
            {treatment_facts.procedures?.map((proc: string, idx: number) => (
              <Box key={`p-${idx}`} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                <Box
                  sx={{
                    width: 5,
                    height: 5,
                    borderRadius: '50%',
                    bgcolor: theme.palette.warning.main,
                    mt: '7px',
                    flexShrink: 0,
                  }}
                />
                <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.45 }}>
                  {proc}
                </Typography>
              </Box>
            ))}
          </Stack>
        </SidebarCard>
      )}

      {/* ── Modalities ── */}
      {treatment_modalities && treatment_modalities.length > 0 && (
        <SidebarCard
          icon={<PsychIcon sx={{ fontSize: 15 }} />}
          title="Modalities"
          defaultExpanded={false}
        >
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {treatment_modalities.map((m: string, idx: number) => (
              <Chip
                key={idx}
                label={m}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.secondary.main, 0.08),
                  fontSize: '0.7rem',
                  height: 24,
                }}
              />
            ))}
          </Box>
        </SidebarCard>
      )}

      {/* ── Activities ── */}
      {activities_performed && activities_performed.length > 0 && (
        <SidebarCard
          icon={<TaskIcon sx={{ fontSize: 15 }} />}
          title="Activities"
          badge={
            <Chip
              label={activities_performed.length}
              size="small"
              sx={{
                height: 18,
                minWidth: 18,
                fontSize: '0.65rem',
                fontWeight: 700,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                color: theme.palette.primary.main,
              }}
            />
          }
          defaultExpanded={false}
        >
          <Stack spacing={0.25}>
            {activities_performed.map((a: string, idx: number) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <CheckIcon
                  sx={{ fontSize: 13, color: theme.palette.success.main, mt: '3px', flexShrink: 0 }}
                />
                <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
                  {a}
                </Typography>
              </Box>
            ))}
          </Stack>
        </SidebarCard>
      )}

      {/* ── Attestations ── */}
      {attestations && attestations.length > 0 && (
        <SidebarCard
          icon={<ClinicalIcon sx={{ fontSize: 15 }} />}
          title="Attestations"
          defaultExpanded={false}
        >
          <Stack spacing={0.25}>
            {attestations.map((att: string, idx: number) => (
              <Box key={idx} sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
                <CheckIcon
                  sx={{ fontSize: 13, color: theme.palette.primary.main, mt: '3px', flexShrink: 0 }}
                />
                <Typography variant="body2" sx={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
                  {att}
                </Typography>
              </Box>
            ))}
          </Stack>
        </SidebarCard>
      )}

      {/* ── Participants ── */}
      {participants && participants.length > 0 && (
        <SidebarCard
          icon={<GroupsIcon sx={{ fontSize: 15 }} />}
          title="Participants"
          defaultExpanded={false}
        >
          <Stack spacing={0.5}>
            {participants.map((p: any, idx: number) => (
              <Box key={idx}>
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.78rem' }}>
                  {p.role}{p.count && p.count > 1 ? ` (${p.count})` : ''}
                </Typography>
                {p.notes && (
                  <Typography
                    variant="caption"
                    sx={{ color: theme.palette.text.secondary, lineHeight: 1.35, display: 'block', fontSize: '0.72rem' }}
                  >
                    {p.notes}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </SidebarCard>
      )}

      {/* ── Data Reviewed ── */}
      {data_reviewed && data_reviewed.length > 0 && (
        <SidebarCard
          icon={<DataIcon sx={{ fontSize: 15 }} />}
          title="Data Reviewed"
          defaultExpanded={false}
        >
          <Stack spacing={0.75}>
            {data_reviewed.map((d: any, idx: number) => (
              <Box key={idx}>
                <Chip
                  label={formatSnakeCase(d.type)}
                  size="small"
                  variant="outlined"
                  sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, mb: 0.25 }}
                />
                {d.details && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: theme.palette.text.secondary,
                      lineHeight: 1.35,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      fontSize: '0.72rem',
                    }}
                  >
                    {d.details}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        </SidebarCard>
      )}
    </Stack>
  );
};
